import { useAuth } from '@/context/AuthContext';
import React, { useEffect, useState, useRef } from 'react'
import { Input } from '../ui/input';

const LinkInput = ({ editLinkInput, editor, isLinkInputOpen = false, setIsLinkInputOpen }) => {
    const [link, setLink] = useState('');
    const { axiosInstance } = useAuth();
    const [searchResults, setSearchResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const wrapperRef = useRef(null);

    // Add click outside listener
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsLinkInputOpen(false);
                setLink('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLinkChange = (e) => {
        setLink(e.target.value);
    }

    const handleLinkSubmit = (e, link) => {
        e.preventDefault();
        if (link){
            editor.chain().extendMarkRange('link').setLink({ href: link }).run();
            setLink('');
            setIsLinkInputOpen(false);
        } else {
            setError('Please enter a valid link');
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleLinkSubmit(e, link);
            setIsLinkInputOpen(false);
        }
        if (e.key === 'Escape') {
            setLink('');
            setIsLinkInputOpen(false);
        }
    }

    const handleResultClick = (e, result) => {
        setSearchResults([]);
        handleLinkSubmit(e, result.key);
    }

    const handleSearch = async () => {
        if (!link.trim()) return;
        setSearchResults([]);

        try {
            const response = await axiosInstance.post('/ai-search/typeahead', {
                query: link
            });
            if (response.data.results.length > 0) {
                setSearchResults(response.data.results);
                setError(null);
            } else {
                setError('No results found');
            }
        } catch (error) {
            console.error('Search error:', error);
            setError('An error occurred while searching. Please try again.');
            setSearchResults([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!(link.includes('http://') || link.includes('https://') || link.includes('www.')) && isLinkInputOpen && link.length > 0) {
            setIsLoading(true);
            const timer = setTimeout(() => {
                if (link) {
                    handleSearch();
            }
        }, 300);

        return () => {
            clearTimeout(timer);
        }
        } else {
            setIsLoading(false);
        }
    }, [link]);

    useEffect(() => {
        if (editLinkInput) {
            setLink(editLinkInput);
        }
    }, [editLinkInput]);



  return (
    <div ref={wrapperRef} className='absolute top-full left-3/4 w-72 z-50'>
      <div className="relative">
        <Input
          className='w-full p-2 border rounded-lg border-zinc-800 dark:bg-zinc-950 dark:text-zinc-100
            placeholder-zinc-500 shadow-lg'
          type="text"
          value={link}
          onChange={handleLinkChange}
          onKeyDown={handleKeyDown}
          placeholder="Enter URL or task key"
          autoFocus
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="animate-spin h-5 w-5 border-2 border-zinc-500 rounded-full border-t-indigo-500"/>
          </div>
        )}
      </div>
      {searchResults.length > 0 && (
        <div className='mt-1 bg-white dark:bg-zinc-950 border border-zinc-800 rounded-lg shadow-lg max-h-60 overflow-y-auto'>
          {searchResults.map((result) => (
            <div
              key={result.id}
              onClick={(e) => handleResultClick(e, result)}
              className='p-2 hover:bg-zinc-200 dark:hover:bg-zinc-800 cursor-pointer border-b border-zinc-800 last:border-b-0 transition-colors duration-200'
            >
              <div className='font-medium text-zinc-900 dark:text-zinc-100'>
                {result.key}
              </div>
              <div className='text-sm text-zinc-600 dark:text-zinc-400'>
                {result.summary}
              </div>
            </div>
          ))}
        </div>
      )}
      {error && (
        <div className='mt-1 p-2 text-red-400 bg-red-950/20 border border-red-800/50 rounded-lg'>
          {error}
        </div>
      )}
    </div>
  )
}

export default LinkInput